import React, { useRef, useState, useEffect } from "react"
import { Canvas, useFrame } from "@react-three/fiber"
import { OrbitControls, useGLTF } from "@react-three/drei"
import { FaExpand, FaCompress, FaSearchPlus, FaSearchMinus, FaArrowLeft } from "react-icons/fa"

function Boat({ scale = 1.0 }) {
  const { scene } = useGLTF('/models/boat.glb')
  const boatRef = useRef()
  
  useFrame((state) => {
    if (boatRef.current) {
      boatRef.current.position.y = 0.5 + Math.sin(state.clock.elapsedTime * 0.8) * 0.12
    }
  })

  return <primitive ref={boatRef} object={scene} scale={scale} position={[0, 0, 0]} />
}

function Water() {
  const waterRef = useRef()
  
  useFrame((state) => {
    if (waterRef.current) {
      const positions = waterRef.current.geometry.attributes.position.array
      const time = state.clock.elapsedTime
      for (let i = 0; i < positions.length; i += 3) {
        const x = positions[i]
        const y = positions[i + 1]
        positions[i + 2] = Math.sin(x * 0.3 + time) * 0.15 + Math.cos(y * 0.3 + time * 0.7) * 0.15
      }
      waterRef.current.geometry.attributes.position.needsUpdate = true
    }
  })

  return (
    <mesh ref={waterRef} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
      <planeGeometry args={[100, 100, 50, 50]} />
      <meshStandardMaterial 
        color="#006994" 
        metalness={0.3} 
        roughness={0.5} 
        transparent 
        opacity={0.9} 
      />
    </mesh>
  )
}

export default function Boat3D({ prototype }) {
  const [isLoading, setIsLoading] = useState(true)
  const [showHint, setShowHint] = useState(true)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const canvasContainerRef = useRef(null)
  const controlsRef = useRef()
  const canvasRef = useRef()
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 })

  useEffect(() => {
    const timer = setTimeout(() => setShowHint(false), 5000)
    return () => clearTimeout(timer)
  }, [])

  const handleZoomIn = () => {
    if (controlsRef.current) {
      const camera = controlsRef.current.object
      camera.position.multiplyScalar(0.8)
      controlsRef.current.update()
    }
  }

  const handleZoomOut = () => {
    if (controlsRef.current) {
      const camera = controlsRef.current.object
      camera.position.multiplyScalar(1.2)
      controlsRef.current.update()
    }
  }

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      canvasContainerRef.current?.requestFullscreen()
      setIsFullscreen(true)
    } else {
      document.exitFullscreen()
      setIsFullscreen(false)
    }
  }

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFS = !!document.fullscreenElement
      setIsFullscreen(isFS)
      setTimeout(() => {
        if (canvasContainerRef.current) {
          const rect = canvasContainerRef.current.getBoundingClientRect()
          setCanvasSize({ width: rect.width, height: rect.height })
        }
      }, 150)
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  return (
    <main className="flex flex-col-reverse md:flex-row w-full min-h-screen bg-white font-custom overflow-x-hidden">
        
        {/* 3D Model Section */}
        <div 
          ref={canvasContainerRef}
          className="relative w-full h-[65vh] md:h-screen md:flex-1 order-1 md:order-2 bg-slate-50 cursor-grab active:cursor-grabbing"
        >
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-sky-100 z-30">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          
          {/* Exit fullscreen button */}
          {isFullscreen && (
            <button
              onClick={toggleFullscreen}
              className="absolute top-4 left-4 z-40 bg-black/70 hover:bg-black/90 text-white px-3 py-2 rounded-full text-sm flex items-center gap-2 transition-all shadow-lg"
            >
              <FaArrowLeft size={14} />
              Back
            </button>
          )}

          {/* Interactive Hint */}
          {showHint && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-40 bg-black/70 text-white px-4 py-2 rounded-full text-sm flex items-center gap-2 animate-pulse whitespace-nowrap">
              <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Drag to rotate • Scroll to zoom
            </div>
          )}
          
          {/* Control Buttons */}
          <div className="absolute bottom-4 right-4 flex gap-2 z-30">
            <button
              onClick={handleZoomOut}
              className="bg-white/80 hover:bg-white text-slate-700 p-2 rounded-full shadow-lg transition-all hover:scale-110"
              title="Zoom Out"
            >
              <FaSearchMinus size={16} />
            </button>
            <button
              onClick={handleZoomIn}
              className="bg-white/80 hover:bg-white text-slate-700 p-2 rounded-full shadow-lg transition-all hover:scale-110"
              title="Zoom In"
            >
              <FaSearchPlus size={16} />
            </button>
            <button
              onClick={toggleFullscreen}
              className="bg-white/80 hover:bg-white text-slate-700 p-2 rounded-full shadow-lg transition-all hover:scale-110"
              title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
            >
              <FaExpand size={16} />
            </button>
          </div>
          
          <Canvas
            key={`${canvasSize.width}-${canvasSize.height}`}
            ref={canvasRef}
            camera={{ position: [-3.5, 4.5, -3.5], fov: 70 }}
            onCreated={() => setIsLoading(false)}
            shadows
            style={{ background: '#87ceeb' }}
          >
            <fog attach="fog" args={['#87ceeb', 10, 60]} />
            
            {/* Lighting */}
            <directionalLight position={[10, 15, 10]} intensity={1.8} castShadow />
            <directionalLight position={[-15, 5, 5]} intensity={1.2} />
            <hemisphereLight color="#ffffff" groundColor="#006994" intensity={0.8} />
            
            <Water />
            <Boat />
            
            <OrbitControls 
              ref={controlsRef}
              enableDamping 
              dampingFactor={0.05}
              minDistance={1}
              maxDistance={30}
              enablePan={true}
            />
          </Canvas>
          
          {/* Permanent hint at bottom */}
          <div className="absolute bottom-4 left-4 text-white/80 text-xs flex items-center gap-1 pointer-events-none">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
            </svg>
            Interactive 3D Model - Drag to Explore
          </div>
        </div>

        {/* Info Section */}
        <div className="w-full md:w-[450px] p-8 md:p-12 flex flex-col justify-center bg-white shadow-2xl z-20 order-2 md:order-1">
          <h1 className="font-bold text-4xl md:text-7xl text-slate-900 mb-4">
            {prototype.title}
          </h1>
        
          <p className="text-slate-600 text-lg mb-8 leading-relaxed">
            {prototype.description}
          </p>

          {/* Stats Grid */}
          <div className="flex flex-wrap justify-start gap-8 pt-6 border-t border-slate-100">
            {prototype.topics && prototype.topics.map((topic, idx) => (
              <div key={idx} className="flex flex-col items-center min-w-[70px]">
                {topic.icon && (
                  <img 
                    src={topic.icon} 
                    alt={topic.title} 
                    className="w-6 h-6 mb-1 grayscale opacity-80" 
                  />
                )}
                <span className="text-slate-900 text-2xl font-bold">{topic.value}</span>
                <span className="text-slate-400 text-[10px] uppercase font-bold tracking-tighter">
                  {topic.title}
                </span>
              </div>
            ))}
          </div>
        </div>

      </main>
  )
}

useGLTF.preload('/models/boat.glb')
